import React, { useEffect, useState } from 'react';
import api from '../services/api.jsx';

const emptySelection = { toAdd: {}, toDeactivate: {}, toReactivate: {} };

const ImportUsersModal = ({ show, onClose, onImportComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [selection, setSelection] = useState(emptySelection);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (show) {
      loadPreview();
    } else {
      setPreview(null);
      setSelection(emptySelection);
      setResult(null);
      setError('');
    }
  }, [show]);

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/users/import-preview');
      setPreview(res.data);
      // Default every proposed change to selected; admin can uncheck specific users before confirming.
      setSelection({
        toAdd: Object.fromEntries((res.data.toAdd || []).map((u) => [u.userId, true])),
        toDeactivate: Object.fromEntries((res.data.toDeactivate || []).map((u) => [u.userId, true])),
        toReactivate: Object.fromEntries((res.data.toReactivate || []).map((u) => [u.userId, true]))
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load import preview');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (section, userId) => {
    setSelection((prev) => ({
      ...prev,
      [section]: { ...prev[section], [userId]: !prev[section][userId] }
    }));
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    setError('');
    try {
      const addUsers = (preview.toAdd || []).filter((u) => selection.toAdd[u.userId]);
      const deactivateUserIds = (preview.toDeactivate || []).filter((u) => selection.toDeactivate[u.userId]).map((u) => u.userId);
      const reactivateUserIds = (preview.toReactivate || []).filter((u) => selection.toReactivate[u.userId]).map((u) => u.userId);

      const res = await api.post('/admin/users/import', { addUsers, deactivateUserIds, reactivateUserIds });
      setResult(res.data);
      onImportComplete?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply import');
    } finally {
      setImporting(false);
    }
  };

  if (!show) return null;

  const renderSection = (title, section, items, emptyLabel) => (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ marginBottom: '6px' }}>{title} ({items.length})</h4>
      {items.length === 0 ? (
        <div style={{ color: '#777', fontSize: '13px' }}>{emptyLabel}</div>
      ) : (
        <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
          {items.map((item) => (
            <label
              key={item.userId}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}
            >
              <input
                type="checkbox"
                checked={!!selection[section][item.userId]}
                onChange={() => toggle(section, item.userId)}
              />
              <span>{item.displayName || item.email} <span style={{ color: '#999' }}>({item.email})</span></span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white', padding: '30px', borderRadius: '8px',
        maxWidth: '600px', width: '90%', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ marginTop: 0 }}>Import from Azure AD</h2>
        <p style={{ color: '#555', marginBottom: '12px' }}>
          Compares Azure AD against local users. Uncheck any user you don't want to include, then confirm.
        </p>

        {loading && <div>Loading preview...</div>}
        {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

        {result && (
          <div style={{ marginBottom: '16px', color: 'green' }}>
            Import complete: {result.added} added, {result.deactivated} deactivated, {result.reactivated} reactivated.
          </div>
        )}

        {!loading && preview && !result && (
          <>
            {renderSection('New users to add', 'toAdd', preview.toAdd || [], 'No new active Azure AD users found.')}
            {renderSection('Users to deactivate', 'toDeactivate', preview.toDeactivate || [], 'No local users are missing/disabled in Azure AD.')}
            {renderSection('Users to reactivate', 'toReactivate', preview.toReactivate || [], 'No previously deactivated users are active again.')}
          </>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
          {!result && (
            <button
              onClick={handleImport}
              className="btn btn-primary"
              disabled={loading || importing || !preview}
            >
              {importing ? 'Importing...' : 'Confirm Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportUsersModal;
